import React from 'react';
import { Link } from 'react-router-dom';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { Button } from '../components/ui/button';
import { 
  Heart, 
  Users, 
  Target, 
  Handshake, 
  Globe, 
  TrendingUp,
  CheckCircle,
  ArrowRight
} from 'lucide-react';

const AboutPage = () => {
  return (
    <div className="min-h-screen bg-stone-white" data-testid="about-page">
      <Navbar />

      {/* Hero Section */}
      <section className="pt-24 pb-16 md:pt-32 md:pb-24 bg-gradient-to-br from-emerald-deep/5 via-transparent to-legacy-gold/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <div className="inline-flex items-center gap-2 bg-emerald-deep/10 text-emerald-deep px-4 py-2 rounded-full text-sm font-medium mb-6">
              <Heart className="w-4 h-4" />
              <span>Our Story</span>
            </div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-stone-900 tracking-tight mb-6">
              Empowering <span className="text-emerald-deep">Black-Owned</span> Businesses
            </h1>
            <p className="text-lg sm:text-xl text-stone-600 mb-8">
              Uprize Black Biz is dedicated to connecting communities with exceptional 
              Black-owned businesses across the United States and beyond.
            </p>
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="font-display text-3xl md:text-4xl font-bold text-stone-900 mb-6">
                Our Mission
              </h2>
              <p className="text-lg text-stone-600 mb-6">
                We believe in the power of community and the importance of supporting 
                Black-owned businesses. Our platform serves as a bridge between 
                entrepreneurs and consumers who want to make a difference with their 
                purchasing decisions.
              </p>
              <p className="text-lg text-stone-600 mb-8">
                By providing visibility and access, we help Black business owners 
                thrive while enabling customers to discover amazing products and 
                services they might otherwise never find.
              </p>
              <div className="flex flex-wrap gap-4">
                <div className="flex items-center gap-2 text-emerald-deep">
                  <CheckCircle className="w-5 h-5" />
                  <span className="font-medium">Community First</span>
                </div>
                <div className="flex items-center gap-2 text-emerald-deep">
                  <CheckCircle className="w-5 h-5" />
                  <span className="font-medium">Economic Empowerment</span>
                </div>
                <div className="flex items-center gap-2 text-emerald-deep">
                  <CheckCircle className="w-5 h-5" />
                  <span className="font-medium">Nationwide Reach</span>
                </div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-emerald-deep/5 rounded-2xl p-6 text-center">
                <Target className="w-10 h-10 text-emerald-deep mx-auto mb-3" />
                <h3 className="font-display text-lg font-semibold text-stone-900 mb-2">Vision</h3>
                <p className="text-sm text-stone-600">A world where Black-owned businesses flourish</p>
              </div>
              <div className="bg-legacy-gold/10 rounded-2xl p-6 text-center">
                <Handshake className="w-10 h-10 text-legacy-gold mx-auto mb-3" />
                <h3 className="font-display text-lg font-semibold text-stone-900 mb-2">Values</h3>
                <p className="text-sm text-stone-600">Trust, community, and excellence</p>
              </div>
              <div className="bg-legacy-gold/10 rounded-2xl p-6 text-center">
                <Globe className="w-10 h-10 text-legacy-gold mx-auto mb-3" />
                <h3 className="font-display text-lg font-semibold text-stone-900 mb-2">Reach</h3>
                <p className="text-sm text-stone-600">Connecting communities worldwide</p>
              </div>
              <div className="bg-emerald-deep/5 rounded-2xl p-6 text-center">
                <TrendingUp className="w-10 h-10 text-emerald-deep mx-auto mb-3" />
                <h3 className="font-display text-lg font-semibold text-stone-900 mb-2">Growth</h3>
                <p className="text-sm text-stone-600">Supporting sustainable success</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What We Do Section */}
      <section className="py-16 md:py-24 bg-stone-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-display text-3xl md:text-4xl font-bold text-stone-900 mb-4">
              What We Do
            </h2>
            <p className="text-lg text-stone-600 max-w-2xl mx-auto">
              We provide the tools and platform for Black-owned businesses to succeed
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white rounded-2xl p-8 shadow-sm">
              <div className="w-14 h-14 bg-emerald-deep/10 rounded-xl flex items-center justify-center mb-6">
                <Users className="w-7 h-7 text-emerald-deep" />
              </div>
              <h3 className="font-display text-xl font-semibold text-stone-900 mb-3">
                Connect Communities
              </h3>
              <p className="text-stone-600">
                We bridge the gap between Black-owned businesses and customers 
                who want to support them, creating meaningful economic connections.
              </p>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-sm">
              <div className="w-14 h-14 bg-legacy-gold/10 rounded-xl flex items-center justify-center mb-6">
                <Globe className="w-7 h-7 text-legacy-gold" />
              </div>
              <h3 className="font-display text-xl font-semibold text-stone-900 mb-3">
                Increase Visibility
              </h3>
              <p className="text-stone-600">
                Our directory helps businesses get discovered by customers 
                actively looking to support Black entrepreneurs across all industries.
              </p>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-sm">
              <div className="w-14 h-14 bg-emerald-deep/10 rounded-xl flex items-center justify-center mb-6">
                <TrendingUp className="w-7 h-7 text-emerald-deep" />
              </div>
              <h3 className="font-display text-xl font-semibold text-stone-900 mb-3">
                Drive Growth
              </h3>
              <p className="text-stone-600">
                Through featured listings and promotional tools, we help 
                businesses expand their reach and grow their customer base.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Important Notice */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-emerald-deep/5 border border-emerald-deep/20 rounded-2xl p-8 md:p-12 text-center">
            <h2 className="font-display text-2xl md:text-3xl font-bold text-stone-900 mb-4">
              Exclusively for Black-Owned Businesses
            </h2>
            <p className="text-lg text-stone-600 mb-6">
              Uprize Black Biz is a dedicated platform exclusively for Black-owned 
              businesses. We verify and celebrate businesses owned and operated by 
              Black entrepreneurs, ensuring our community can shop with confidence 
              and purpose.
            </p>
            <div className="bg-white rounded-xl p-6 text-left space-y-4">
              <h3 className="font-semibold text-stone-900">Listing Requirements:</h3>
              <ul className="text-stone-600 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-emerald-deep flex-shrink-0 mt-0.5" />
                  <span>Business must be Black-owned (majority ownership by Black individuals)</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-emerald-deep flex-shrink-0 mt-0.5" />
                  <span>Business must be legally registered with your state and maintain active status</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-emerald-deep flex-shrink-0 mt-0.5" />
                  <span>Food trucks and restaurants must have current, valid permits and all required licenses</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-emerald-deep flex-shrink-0 mt-0.5" />
                  <span>Businesses serving alcohol must maintain proper, up-to-date alcohol permits and licenses</span>
                </li>
              </ul>
              <p className="text-stone-500 text-xs mt-4 pt-4 border-t border-stone-200">
                <strong>Note:</strong> If a business is found to not meet these requirements, the listing will be 
                deactivated and all purchases will be refunded. You may re-register once all legal requirements 
                are properly met. We prioritize customer satisfaction and safety above all.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 bg-emerald-deep">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-3xl md:text-4xl font-bold text-white mb-6">
            Ready to Join Our Community?
          </h2>
          <p className="text-emerald-100/80 text-lg mb-8 max-w-2xl mx-auto">
            Whether you're a business owner looking to expand your reach or a 
            customer wanting to support Black excellence, we welcome you.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/register">
              <Button className="bg-white text-emerald-deep hover:bg-stone-50 rounded-full px-8 py-6 text-lg font-semibold">
                List Your Business <ArrowRight className="w-5 h-5 ml-2 inline" />
              </Button>
            </Link>
            <Link to="/directory">
              <Button variant="outline" className="border-white text-white hover:bg-white/10 rounded-full px-8 py-6 text-lg font-semibold">
                Explore Directory
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AboutPage;
